import { environment } from '../../../../../src/environments/environment'

export class CRBConfigDto {
  rId: number
  name: string
  usernamePrefix: string
  domain: string
  oktaCustomDomain: string
  programCode: string
}

/** Light and dark values of the same keys. */
export interface ScopedColors {
  light: Record<string, string>
  dark: Record<string, string>
}

/**
 * Everything a program can configure, as stored. The sections are fixed and the keys inside
 * them are not, so adding a colour or a piece of copy is a change here and in the editor only.
 */
export interface SettingsDocument {
  theme: ScopedColors
  elements: ScopedColors
  content: Record<string, string>
  documents: Record<string, string>
  features: Record<string, boolean>
  /** Blob paths by slot, a list because a slot such as the marketing banner holds several. */
  images: Record<string, string[]>
}

export const emptySettingsDocument = (): SettingsDocument => ({
  theme: { light: {}, dark: {} },
  elements: { light: {}, dark: {} },
  content: {},
  documents: {},
  features: {},
  images: {},
})

/** Fills in whatever a stored document leaves out, so callers can write into any section. */
export const normalizeSettingsDocument = (document: SettingsDocument): SettingsDocument => ({
  theme: { light: { ...document?.theme?.light }, dark: { ...document?.theme?.dark } },
  elements: { light: { ...document?.elements?.light }, dark: { ...document?.elements?.dark } },
  content: { ...document?.content },
  documents: { ...document?.documents },
  features: { ...document?.features },
  images: { ...document?.images },
})

export class CRBSettingKeys {
  // Colors
  static GlobalHeaderColor = 'globalHeaderColor'
  static CallToActionColor = 'callToActionColor'
  static TopNavigationBarColor = 'topNavigationBarColor'
  static ActiveNavFunctionColor = 'activeNavFunctionColor'
  static SecondaryFunctionColor = 'secondaryFunctionColor'
  static ModuleHeaderColor = 'moduleHeaderColor'
  static ModuleHeaderTextColor = 'moduleHeaderTextColor'
  static BottomNavigationColor = 'bottomNavigationColor'
  static SocialMediaIconColor = 'socialMediaIconColor'
  static DividerLineColor = 'dividerLineColor'
  static MainTextColor = 'mainTextColor'
  static ExternalBankTransferIconColor = 'externalBankTransferIconColor'
  static MenuLinkHoverColor = 'menuLinkHoverColor'
  static WriteOffHomeTitleTextColor = 'writeOffHomeTitleTextColor'
  static WriteOffActivityDetailsTextColor = 'writeOffActivityDetailsTextColor'
  static AuthBackgroundColor = 'authBackgroundColor'
  static MainBackgroundColor = 'mainBackgroundColor'
  static RegisterButtonColor = 'registerButtonColor'
  static LoginButtonColor = 'loginButtonColor'
  static ErrorTextColor = 'errorTextColor'
  static ErrorBackgroundColor = 'errorBackgroundColor'
  static TabInactiveColor = 'tabInactiveColor'
  static TabInactiveTextColor = 'tabInactiveTextColor'
  static TabActiveTextColor = 'tabActiveTextColor'
  static InputBackgroundColor = 'inputBackgroundColor'
  static InputTextColor = 'inputTextColor'
  static InputBorderColor = 'inputBorderColor'

  // Other Settings
  static BankSupportPhoneNumber = 'bankSupportPhoneNumber'
  static FooterCopy = 'footerCopy'
  static ProgramName = 'programName'
  static IntraFiDescription = 'intraFiDescription'
  static IntraFiOptOutBalanceMessage = 'intraFiOptOutBalanceMessage'
  static IntraFiOptOutSweptBalanceMessage = 'intraFiOptOutSweptBalanceMessage'
  static IsTransferToBankEnabled = 'isTransferToBankEnabled'
  static IsOperatorSiteLinkEnabled = 'isOperatorSiteLinkEnabled'
  static ExternalBankTransferInfoHeader = 'externalBankTransferInfoHeader'
  static ExternalBankTransferInfoBody = 'externalBankTransferInfoBody'
  static OperatorSiteLinkVerbiage = 'operatorSiteLinkVerbiage'
  static OperatorSiteLinkText = 'operatorSiteLinkText'
  static OperatorSiteLinkUrl = 'operatorSiteLinkUrl'
  static HomeAvailableBalanceWriteOffTitle = 'homeAvailableBalanceWriteOffTitle'
  static HomeAvailableBalanceWriteOffText = 'homeAvailableBalanceWriteOffText'
  static HomeAvailableBalanceWriteOffButtonText = 'homeAvailableBalanceWriteOffButtonText'
  static HomeAvailableBalanceWriteOffHelpText = 'homeAvailableBalanceWriteOffHelpText'
  static ActivityDetailsWriteOffWarningText = 'activityDetailsWriteOffWarningText'
  static EmulateCustomerHasWriteOff = 'emulateCustomerHasWriteOff'
  static ContactUsTitleText = 'contactUsTitleText'
  static ContactUsText = 'contactUsText'

  // hubspot documents
  static WebsiteTermsOfUse = 'websiteTermsOfUse'
  static CRBPrivacyPolicy = 'cRBPrivacyPolicy'
  static ProgramPrivacyPolicy = 'programPrivacyPolicy'
  static ProgramFAQ = 'programFAQ'
  static ProgramDepositAgreement = 'programDepositAgreement'
  static PortalFAQ = 'portalFAQ'
  static IntraFiCashServiceDepositPlacementAgreement = 'intraFiCashServiceDepositPlacementAgreement'
  static InsuredCashSweepCustodialAgreement = 'insuredCashSweepCustodialAgreement'
  static MarketingPartnerPrivacyPolicy = 'marketingPartnerPrivacyPolicy'
}

/** What the API says about a document link. `reason` is a code, not a message. */
export interface DocumentLinkStatus {
  reachable: boolean
  statusCode?: number
  reason: string
  contentType?: string
  finalUrl?: string
}

export type SettingsSection = 'content' | 'documents' | 'features'

/** Links to a hosted file, kept apart from copy so they can be validated as URLs. */
export const DocumentSettingKeys: string[] = [
  CRBSettingKeys.WebsiteTermsOfUse,
  CRBSettingKeys.CRBPrivacyPolicy,
  CRBSettingKeys.ProgramPrivacyPolicy,
  CRBSettingKeys.ProgramFAQ,
  CRBSettingKeys.ProgramDepositAgreement,
  CRBSettingKeys.PortalFAQ,
  CRBSettingKeys.IntraFiCashServiceDepositPlacementAgreement,
  CRBSettingKeys.InsuredCashSweepCustodialAgreement,
  CRBSettingKeys.MarketingPartnerPrivacyPolicy,
]

/** Stored as booleans rather than the string "true". */
export const FeatureSettingKeys: string[] = [
  CRBSettingKeys.IsTransferToBankEnabled,
  CRBSettingKeys.IsOperatorSiteLinkEnabled,
  CRBSettingKeys.EmulateCustomerHasWriteOff,
]

export const sectionOf = (key: string): SettingsSection =>
  DocumentSettingKeys.includes(key) ? 'documents'
    : FeatureSettingKeys.includes(key) ? 'features'
      : 'content'

export const BrandSettingKeys: string[] = [
  CRBSettingKeys.BankSupportPhoneNumber,
  CRBSettingKeys.FooterCopy,
  CRBSettingKeys.ProgramName,
  CRBSettingKeys.IntraFiDescription,
  CRBSettingKeys.IntraFiOptOutBalanceMessage,
  CRBSettingKeys.IntraFiOptOutSweptBalanceMessage,
  CRBSettingKeys.IsTransferToBankEnabled,
  CRBSettingKeys.IsOperatorSiteLinkEnabled,
  CRBSettingKeys.ExternalBankTransferInfoHeader,
  CRBSettingKeys.ExternalBankTransferInfoBody,
  CRBSettingKeys.OperatorSiteLinkVerbiage,
  CRBSettingKeys.OperatorSiteLinkText,
  CRBSettingKeys.OperatorSiteLinkUrl,
  CRBSettingKeys.HomeAvailableBalanceWriteOffTitle,
  CRBSettingKeys.HomeAvailableBalanceWriteOffText,
  CRBSettingKeys.HomeAvailableBalanceWriteOffButtonText,
  CRBSettingKeys.HomeAvailableBalanceWriteOffHelpText,
  CRBSettingKeys.ActivityDetailsWriteOffWarningText,
  CRBSettingKeys.EmulateCustomerHasWriteOff,
  CRBSettingKeys.ContactUsTitleText,
  CRBSettingKeys.ContactUsText,

  CRBSettingKeys.WebsiteTermsOfUse,
  CRBSettingKeys.CRBPrivacyPolicy,
  CRBSettingKeys.ProgramPrivacyPolicy,
  CRBSettingKeys.ProgramFAQ,
  CRBSettingKeys.ProgramDepositAgreement,
  CRBSettingKeys.PortalFAQ,
  CRBSettingKeys.IntraFiCashServiceDepositPlacementAgreement,
  CRBSettingKeys.InsuredCashSweepCustodialAgreement,
  CRBSettingKeys.MarketingPartnerPrivacyPolicy
]

/**
 * Named colours of the theme. Elements point at these instead of carrying their own hex, so a
 * brand is set up by choosing a handful of colours rather than twenty six.
 *
 * The stored keys all contain "Color" on purpose: that is how the API decides which settings
 * belong to the branded pages, so a new theme colour needs no change on the server.
 */
export interface ThemeColorDefinition {
  /** Name used in references and in the editor, e.g. "primary". */
  name: string
  /** Key this is stored under. */
  key: string
  default: string
  darkDefault?: string
  /** Reads better grouped in pairs in the editor. */
  pairedWith?: string
  /**
   * Brand colours are the handful somebody actually decides; the rest follow from them and are
   * kept out of the way. Both are editable, the split is about what gets asked first.
   */
  group: 'brand' | 'supporting'
}

export const ThemeColorDefinitions: ThemeColorDefinition[] = [
  { name: 'primary', key: 'themePrimaryColor', default: '#ed3186', group: 'brand' },
  { name: 'secondary', key: 'themeSecondaryColor', default: '#1dade4', group: 'brand' },
  { name: 'background', key: 'themeBackgroundColor', default: '#ffffff', darkDefault: '#121212', group: 'brand' },
  { name: 'foreground', key: 'themeForegroundColor', default: '#222222', darkDefault: '#dbdbdb', pairedWith: 'background', group: 'supporting' },
  { name: 'secondaryForeground', key: 'themeSecondaryForegroundColor', default: '#ffffff', pairedWith: 'secondary', group: 'supporting' },
  { name: 'muted', key: 'themeMutedColor', default: '#f2f2f2', darkDefault: '#2a2a2a', group: 'supporting' },
  { name: 'mutedForeground', key: 'themeMutedForegroundColor', default: '#5c5c5c', darkDefault: '#b3b3b3', pairedWith: 'muted', group: 'supporting' },
  { name: 'accent', key: 'themeAccentColor', default: '#fd8924', group: 'brand' },
  { name: 'header', key: 'themeHeaderColor', default: '#222222', group: 'brand' },
  { name: 'headerForeground', key: 'themeHeaderForegroundColor', default: '#ffffff', pairedWith: 'header', group: 'supporting' },
  { name: 'border', key: 'themeBorderColor', default: '#b9b9b9', darkDefault: '#414141', group: 'supporting' },
  // Semantic rather than brand: it means danger, so it is not something a brand picks to match.
  { name: 'destructive', key: 'themeDestructiveColor', default: '#d81b60', darkDefault: '#ed3186', group: 'supporting' },
]

export const brandThemeColors = (): ThemeColorDefinition[] =>
  ThemeColorDefinitions.filter(t => t.group === 'brand')

export const supportingThemeColors = (): ThemeColorDefinition[] =>
  ThemeColorDefinitions.filter(t => t.group === 'supporting')

export const themeColor = (name: string): ThemeColorDefinition =>
  ThemeColorDefinitions.find(t => t.name === name)

/** Prefix marking a stored value as pointing at a theme colour rather than being one. */
export const ThemeRefPrefix = 'theme:'

export const themeRef = (name: string): string => `${ThemeRefPrefix}${name}`

/** The theme colour a stored value points at, or null when it is a colour of its own. */
export const themeRefName = (value: string): string =>
  value?.startsWith(ThemeRefPrefix) ? value.slice(ThemeRefPrefix.length) : null

/** Where a colour shows up, so the inspector can label the property it is editing. */
export type ColorRole = 'background' | 'foreground' | 'border'

export interface BrandColorDefinition {
  key: string
  /** Theme colour used when the brand has not chosen something else. */
  defaultRef?: string
  /**
   * Another element colour to follow while nothing has been chosen here. Used where a colour was
   * split out of an existing one: the two stay together until somebody sets them apart, so the
   * split changes no appearance on its own.
   */
  defaultOf?: string
  /** Black or white against another element colour, the way Ionic picks a button label. */
  defaultContrastOf?: string
  /** Literal fallback, for the few colours with no matching theme slot. */
  default?: string
  /** Literal fallback for dark, where reusing the light one would be wrong. */
  darkDefault?: string
  role?: ColorRole
}

/**
 * Every colour an element can use. A colour is added here, used as `var(--<key>)` in the SCSS,
 * and labelled in the i18n file. The resolver, the inspector and the API whitelist all derive
 * from this list, so nothing else needs touching.
 */
export const BrandColorDefinitions: BrandColorDefinition[] = [
  { key: CRBSettingKeys.AuthBackgroundColor, defaultRef: 'background', role: 'background' },
  { key: CRBSettingKeys.MainBackgroundColor, defaultRef: 'background', role: 'background' },
  { key: CRBSettingKeys.MainTextColor, defaultRef: 'foreground', role: 'foreground' },

  { key: CRBSettingKeys.CallToActionColor, defaultRef: 'primary', role: 'background' },
  { key: CRBSettingKeys.LoginButtonColor, defaultOf: CRBSettingKeys.CallToActionColor, role: 'background' },
  { key: 'loginButtonTextColor', defaultContrastOf: CRBSettingKeys.LoginButtonColor, role: 'foreground' },

  { key: CRBSettingKeys.ModuleHeaderColor, defaultRef: 'secondary', role: 'background' },
  { key: CRBSettingKeys.ModuleHeaderTextColor, defaultRef: 'secondaryForeground', role: 'foreground' },
  { key: CRBSettingKeys.RegisterButtonColor, defaultOf: CRBSettingKeys.ModuleHeaderColor, role: 'background' },
  { key: 'registerButtonTextColor', defaultContrastOf: CRBSettingKeys.RegisterButtonColor, role: 'foreground' },

  { key: CRBSettingKeys.GlobalHeaderColor, defaultRef: 'header', role: 'background' },
  { key: CRBSettingKeys.TopNavigationBarColor, defaultRef: 'header', role: 'background' },
  { key: CRBSettingKeys.BottomNavigationColor, defaultRef: 'header', role: 'background' },
  { key: CRBSettingKeys.SecondaryFunctionColor, defaultRef: 'headerForeground', role: 'foreground' },
  { key: CRBSettingKeys.SocialMediaIconColor, defaultRef: 'headerForeground', role: 'foreground' },

  { key: CRBSettingKeys.ActiveNavFunctionColor, defaultRef: 'accent', role: 'background' },
  { key: CRBSettingKeys.MenuLinkHoverColor, defaultRef: 'accent', role: 'foreground' },

  { key: CRBSettingKeys.InputBackgroundColor, defaultRef: 'muted', role: 'background' },
  { key: CRBSettingKeys.InputTextColor, defaultRef: 'foreground', role: 'foreground' },
  { key: CRBSettingKeys.InputBorderColor, defaultRef: 'muted', role: 'border' },

  { key: CRBSettingKeys.TabInactiveColor, defaultRef: 'muted', role: 'background' },
  { key: CRBSettingKeys.TabInactiveTextColor, defaultRef: 'mutedForeground', role: 'foreground' },
  { key: CRBSettingKeys.TabActiveTextColor, defaultContrastOf: CRBSettingKeys.CallToActionColor, role: 'foreground' },

  { key: CRBSettingKeys.DividerLineColor, defaultRef: 'border', role: 'border' },
  { key: CRBSettingKeys.ErrorTextColor, defaultRef: 'destructive', role: 'foreground' },
  // The tinted panel some error messages sit on. No theme slot is a tinted warning surface,
  // and the light tint would read as a bright block in dark, so both are named.
  { key: CRBSettingKeys.ErrorBackgroundColor, default: '#fff2f4', darkDefault: '#3a1f26', role: 'background' },

  { key: CRBSettingKeys.WriteOffHomeTitleTextColor, defaultRef: 'foreground', role: 'foreground' },
  { key: CRBSettingKeys.WriteOffActivityDetailsTextColor, defaultRef: 'foreground', role: 'foreground' },

  // No theme slot fits this one, so it keeps a colour of its own.
  { key: CRBSettingKeys.ExternalBankTransferIconColor, default: '#800080', role: 'foreground' },
]

export const brandColor = (key: string): BrandColorDefinition =>
  BrandColorDefinitions.find(d => d.key === key)

export const WebsiteGlobalFooter = [
  CRBSettingKeys.WebsiteTermsOfUse
]

// Aeropay hosts these publicly and they are the same for every operator, so the
// URLs live in the environment files and only their visibility is configurable
// per operator.
export const AeropayLegalLinks = [
  { key: 'aeropayTermsOfService', url: environment.aeropayTermsOfServiceUrl },
  { key: 'aeropayPrivacyPolicy', url: environment.aeropayPrivacyPolicyUrl },
]

export const PrivacyPolicy = [
  CRBSettingKeys.CRBPrivacyPolicy,
  CRBSettingKeys.ProgramPrivacyPolicy,
  CRBSettingKeys.MarketingPartnerPrivacyPolicy,
]

export const IntraFiAgreement = [
  CRBSettingKeys.IntraFiCashServiceDepositPlacementAgreement,
  CRBSettingKeys.InsuredCashSweepCustodialAgreement,
]

export const InfoActionMenuItems = [
  CRBSettingKeys.ProgramFAQ,
  CRBSettingKeys.ProgramDepositAgreement,
]

export const PreloginInfoItems = [
  CRBSettingKeys.PortalFAQ,
]
